import { isMinigameId } from '../minigames';
import type { HorseStats, StatId } from '../race/types';
import { STAT_IDS, expressedPotential, foundationGenotype, type Genotype } from './genetics';
import { TRAIT_IDS, type TraitId } from './traits';
import { DerbyRngV1, hashFnv1a } from '../rng';
import { makeStuds } from './horse';
import { makeRivals } from './rivals';
import {
  FACILITY_IDS,
  FEED_PLANS,
  LEAGUE_ECONOMY,
  eventDay,
  eventLeague,
  isValidEvent,
  type FacilityId,
  type FeedPlanId,
} from './economy';
import { MAX_NAME_LENGTH, newCareer, rollYearlings, seasonOf } from './horse';
import { RanchSim } from './sim';
import type {
  Horse,
  PendingTraining,
  RaceLogEntry,
  RanchState,
  YearlingLot,
} from './types';

export const SAVE_KEY = 'derbydays:save:v2';
export const BACKUP_KEY = 'derbydays:save:v2:backup';
export const LEGACY_SAVE_KEY = 'derbydays:save:v1';
export const LEGACY_BACKUP_KEY = 'derbydays:save:v1:backup';

/** Phase-2/3 saves carried a single `horse`; phase-4A saves carry the
 * registry. Old shapes normalize on read — never a migration function. */
interface LegacyHorse {
  name: string;
  stats: HorseStats;
  xp: Record<StatId, number>;
}

export interface RanchSaveV1 {
  v: 1;
  meta: { seed: number; tick: number; day: number };
  horses: Horse[];
  featuredId: number | null;
  nextHorseId: number;
  stableLevel: number;
  yearlings: YearlingLot[];
  energy: { current: number; max: number };
  economy: { moneyCents: number; feedPlan: FeedPlanId };
  facilities: Record<FacilityId, number>;
  entries: number[];
  record: {
    starts: number;
    winsByLeague: [number, number, number, number];
    earningsCents: number;
    log: Omit<RaceLogEntry, 'horseId'>[];
  };
  raceDoneToday: boolean;
  drillsToday: number;
  pendingTraining?: Partial<PendingTraining> | null;
  vetLevel?: number;
  patchUpSeason?: number;
  /** Optional 4B fields — absent in 4A saves, normalized on read. */
  studIds?: number[];
  pendingFoals?: { damId: number; sireId: number }[];
  rivals?: Omit<RanchState['rivals'][number], 'statFloor'>[];
  /** Legacy field, read-only. */
  horse?: LegacyHorse;
}

export interface RanchSaveV2 {
  v: 2;
  meta: { seed: number; tick: number; day: number };
  horses: Horse[];
  featuredId: number | null;
  nextHorseId: number;
  stableLevel: number;
  vetLevel: number;
  patchUpSeason: number;
  yearlings: YearlingLot[];
  studIds: number[];
  rivals: RanchState['rivals'];
  pendingFoals: { damId: number; sireId: number }[];
  energy: { current: number; max: number };
  economy: { moneyCents: number; feedPlan: FeedPlanId };
  facilities: Record<FacilityId, number>;
  entries: RanchState['entries'];
  record: RanchState['record'];
  racedHorseIdsToday: number[];
  raceDoneToday: boolean;
  drillsToday: number;
  dailyTrainingCounts: Record<string, number>;
  pendingTraining: PendingTraining | null;
}

let persistenceEnabled = true;

/** ?qa sessions never touch the player's save. */
export function disablePersistence(): void {
  persistenceEnabled = false;
}

/** Pure snapshot — no timestamps, so twin-sim equality tests stay honest. */
export function serializeRanch(sim: RanchSim): RanchSaveV2 {
  const state = sim.state;
  return {
    v: 2,
    meta: { ...state.meta },
    horses: state.horses.map((horse) => ({
      ...horse,
      stats: { ...horse.stats },
      xp: { ...horse.xp },
      potential: { ...horse.potential },
      genotype: JSON.parse(JSON.stringify(horse.genotype)) as Genotype,
      revealedGrades: [...horse.revealedGrades],
      revealedTraits: [...horse.revealedTraits],
      masteredTrainingModes: [...horse.masteredTrainingModes],
      career: { ...horse.career, winsByLeague: [...horse.career.winsByLeague] },
    })),
    featuredId: state.featuredId,
    nextHorseId: state.nextHorseId,
    stableLevel: state.stableLevel,
    yearlings: state.yearlings.map((lot) => ({
      ...lot,
      stats: { ...lot.stats },
      potential: { ...lot.potential },
      estimates: { ...lot.estimates },
      genotype: JSON.parse(JSON.stringify(lot.genotype)) as Genotype,
    })),
    studIds: [...state.studIds],
    rivals: state.rivals.map((info) => ({ ...info, statFloor: { ...info.statFloor } })),
    pendingFoals: state.pendingFoals.map((pending) => ({ ...pending })),
    energy: { ...state.energy },
    economy: { ...state.economy },
    facilities: { ...state.facilities },
    entries: state.entries.map((entry) => ({ ...entry })),
    record: {
      starts: state.record.starts,
      winsByLeague: [...state.record.winsByLeague],
      earningsCents: state.record.earningsCents,
      log: state.record.log.map((entry) => ({ ...entry })),
    },
    racedHorseIdsToday: [...state.racedHorseIdsToday],
    raceDoneToday: state.raceDoneToday,
    drillsToday: state.drillsToday,
    dailyTrainingCounts: { ...state.dailyTrainingCounts },
    pendingTraining: state.pendingTraining ? { ...state.pendingTraining } : null,
    vetLevel: state.vetLevel,
    patchUpSeason: state.patchUpSeason,
  };
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

function clampStats(raw: unknown, fallback: number): HorseStats {
  const source = (raw ?? {}) as Partial<HorseStats>;
  return {
    speed: clampNumber(source.speed, 1, 20, fallback),
    stamina: clampNumber(source.stamina, 1, 20, fallback),
    accel: clampNumber(source.accel, 1, 20, fallback),
    heart: clampNumber(source.heart, 1, 20, fallback),
  };
}

function clampXp(raw: unknown): Record<StatId, number> {
  const source = (raw ?? {}) as Partial<Record<StatId, number>>;
  return {
    speed: clampNumber(source.speed, 0, 500, 0),
    stamina: clampNumber(source.stamina, 0, 500, 0),
    accel: clampNumber(source.accel, 0, 500, 0),
    heart: clampNumber(source.heart, 0, 500, 0),
  };
}

function clampWins(raw: unknown): [number, number, number, number] {
  const source = Array.isArray(raw) ? raw : [];
  return [
    clampNumber(source[0], 0, 10_000, 0),
    clampNumber(source[1], 0, 10_000, 0),
    clampNumber(source[2], 0, 10_000, 0),
    clampNumber(source[3], 0, 10_000, 0),
  ];
}

function clampGenePair(raw: unknown): [number, number] {
  const source = Array.isArray(raw) ? raw : [];
  return [clampNumber(source[0], 0, 1, 0), clampNumber(source[1], 0, 1, 0)];
}

function clampGenotype(raw: unknown, potential: HorseStats, seedKey: string): Genotype {
  const source = raw as Partial<Genotype> | null;
  if (!source || typeof source !== 'object' || !source.stats) {
    // 4A saves carry no genes: synthesize the foundation genotype that
    // reproduces the stored potential exactly — zero behavior change.
    return foundationGenotype(potential, new DerbyRngV1(hashFnv1a(seedKey)));
  }
  const stats = {} as Genotype['stats'];
  for (const stat of STAT_IDS) {
    const pair = (source.stats as Record<string, { a?: number; b?: number }>)[stat] ?? {};
    stats[stat] = {
      a: clampNumber(pair.a, 1, 20, potential[stat]),
      b: clampNumber(pair.b, 1, 20, potential[stat]),
    };
  }
  const traits = {} as Genotype['traits'];
  for (const trait of TRAIT_IDS) {
    traits[trait] = clampGenePair((source.traits as Record<string, unknown> | undefined)?.[trait]);
  }
  const coat = (source.coat ?? {}) as Partial<Genotype['coat']>;
  return {
    stats,
    traits,
    coat: {
      extension: clampGenePair(coat.extension),
      agouti: clampGenePair(coat.agouti),
      gray: clampGenePair(coat.gray),
      cream: clampGenePair(coat.cream),
      markings: clampNumber(coat.markings, 0, 3, 1),
    },
  };
}

function clampHorse(raw: unknown, day: number, worldSeed: number): Horse | null {
  const source = raw as Partial<Horse> | null;
  if (!source || typeof source !== 'object') return null;
  const id = clampNumber(source.id, -8, 1_000_000, 0);
  if (id === 0) return null;
  const career = (source.career ?? {}) as Partial<Horse['career']>;
  const potential = clampStats(source.potential, 20);
  const genotype = clampGenotype(source.genotype, potential, `${worldSeed}:genofix:${id}`);
  const location =
    source.location === 'barn' || source.location === 'sold' || source.location === 'external'
      ? source.location
      : 'featured';
  return {
    id,
    name: String(source.name ?? 'Unnamed').slice(0, MAX_NAME_LENGTH) || 'Unnamed',
    sex: source.sex === 'F' ? 'F' : 'M',
    bornSeason: clampNumber(source.bornSeason, -100, 1_000_000, seasonOf(day) - 3),
    stats: clampStats(source.stats, 2),
    xp: clampXp(source.xp),
    potential: source.genotype ? expressedPotential(genotype) : potential,
    genotype,
    sireId: typeof source.sireId === 'number' ? clampNumber(source.sireId, -8, 1_000_000, 0) || null : null,
    damId: typeof source.damId === 'number' ? clampNumber(source.damId, -8, 1_000_000, 0) || null : null,
    revealedGrades: (Array.isArray(source.revealedGrades) ? source.revealedGrades : []).filter(
      (stat): stat is StatId => (STAT_IDS as readonly string[]).includes(stat as string),
    ),
    revealedTraits: (Array.isArray(source.revealedTraits) ? source.revealedTraits : []).filter(
      (trait): trait is TraitId => (TRAIT_IDS as readonly string[]).includes(trait as string),
    ),
    masteredTrainingModes: (
      Array.isArray(source.masteredTrainingModes) ? source.masteredTrainingModes : []
    ).filter((mode): mode is Horse['masteredTrainingModes'][number] => isMinigameId(mode)),
    bredThisSeason: source.bredThisSeason === true,
    fatigue: clampNumber(source.fatigue, 0, 100, 0),
    injury:
      source.injury && typeof source.injury === 'object'
        ? { daysLeft: clampNumber((source.injury as { daysLeft?: unknown }).daysLeft, 1, 14, 1) }
        : null,
    injuriesEver: clampNumber(source.injuriesEver, 0, 1_000, 0),
    career: {
      starts: clampNumber(career.starts, 0, 1_000_000, 0),
      winsByLeague: clampWins(career.winsByLeague),
      earningsCents: clampNumber(career.earningsCents, 0, 1_000_000_000, 0),
    },
    location,
    retired: source.retired === true,
  };
}

function clampYearling(raw: unknown, worldSeed: number): YearlingLot | null {
  const source = raw as Partial<YearlingLot> | null;
  if (!source || typeof source !== 'object') return null;
  const potential = clampStats(source.potential, 10);
  const genotype = clampGenotype(
    source.genotype,
    potential,
    `${worldSeed}:lotfix:${clampNumber(source.lotId, 0, 100_000_000, 0)}`,
  );
  return {
    lotId: clampNumber(source.lotId, 0, 100_000_000, 0),
    name: String(source.name ?? 'Yearling').slice(0, MAX_NAME_LENGTH) || 'Yearling',
    sex: source.sex === 'F' ? 'F' : 'M',
    stats: clampStats(source.stats, 2),
    potential: source.genotype ? expressedPotential(genotype) : potential,
    estimates: clampStats(source.estimates ?? potential, 10),
    genotype,
    priceCents: clampNumber(source.priceCents, 0, 10_000_000, 30_000),
  };
}

/** Normalize-on-read: every scalar clamped, every list filtered. Hostile
 * or corrupt saves degrade to something playable, never a crash. */
export function deserializeRanch(payload: unknown): RanchSim {
  type RawSave = Partial<RanchSaveV1> &
    Partial<RanchSaveV2> & { v?: unknown; horse?: LegacyHorse };
  const raw = payload as RawSave | null;
  if (!raw || (raw.v !== 1 && raw.v !== 2)) throw new Error('Unsupported save version.');
  const isLegacy = raw.v === 1;
  const storedDay = clampNumber(raw.meta?.day, 1, 1_000_000, 1);
  const oldSeason = Math.floor((storedDay - 1) / 24) + 1;
  const oldSeasonDay = ((storedDay - 1) % 24) + 1;
  const day = isLegacy ? (oldSeason - 1) * 12 + Math.ceil(oldSeasonDay / 2) : storedDay;
  const seed = clampNumber(raw.meta?.seed, 0, 4_294_967_295, 1);
  const feedPlan: FeedPlanId =
    raw.economy?.feedPlan && FEED_PLANS[raw.economy.feedPlan] ? raw.economy.feedPlan : 'hay';
  const maxEnergy = FEED_PLANS[feedPlan].energy;
  const storedEnergyMax = clampNumber(raw.energy?.max, 1, 24, maxEnergy);
  const pendingCost =
    raw.pendingTraining && typeof raw.pendingTraining.cost === 'number'
      ? clampNumber(raw.pendingTraining.cost, 0, 24, 0)
      : 0;
  const refundedStoredEnergy = Math.min(
    storedEnergyMax,
    clampNumber(raw.energy?.current, 0, storedEnergyMax, 0) + pendingCost,
  );
  const currentEnergy = isLegacy
    ? Math.round((refundedStoredEnergy / storedEnergyMax) * maxEnergy)
    : Math.min(maxEnergy, refundedStoredEnergy);

  const facilities = {} as Record<FacilityId, number>;
  for (const id of FACILITY_IDS) {
    facilities[id] = clampNumber((raw.facilities ?? {})[id], 1, 4, 1);
  }

  // The registry — or the phase-3 single horse wrapped into one.
  let horses: Horse[] = (Array.isArray(raw.horses) ? raw.horses : [])
    .map((entry) => clampHorse(entry, day, seed))
    .filter((horse): horse is Horse => horse !== null)
    .slice(0, 64);
  if (horses.length === 0 && raw.horse) {
    const potential = { speed: 20, stamina: 20, accel: 20, heart: 20 };
    horses = [
      {
        id: 1,
        name: String(raw.horse.name ?? 'Unnamed').slice(0, MAX_NAME_LENGTH) || 'Unnamed',
        sex: 'M',
        bornSeason: seasonOf(day) - 3,
        stats: clampStats(raw.horse.stats, 2),
        xp: clampXp(raw.horse.xp),
        potential,
        genotype: foundationGenotype(potential, new DerbyRngV1(hashFnv1a(`${seed}:legacy:1`))),
        sireId: null,
        damId: null,
        revealedGrades: [],
        revealedTraits: [],
        masteredTrainingModes: [],
        bredThisSeason: false,
        fatigue: 0,
        injury: null,
        injuriesEver: 0,
        career: newCareer(),
        location: 'featured',
        retired: false,
      },
    ];
  }
  // The stud registry (negative ids). 4A saves have none — synthesize the
  // same eight the seed would always produce.
  if (!horses.some((horse) => horse.location === 'external')) {
    horses = [...horses, ...makeStuds(seed)];
  }
  let studIds = horses.filter((horse) => horse.location === 'external').map((horse) => horse.id);

  // Rivals: validate against the registry; older saves synthesize a roster.
  const STYLES = ['pacer', 'sprinter', 'closer'];
  type RivalSource = Partial<RanchState['rivals'][number]>;
  let rivals: RanchState['rivals'] = [];
  for (const candidate of (Array.isArray(raw.rivals) ? raw.rivals : []) as unknown[]) {
    if (!candidate || typeof candidate !== 'object') continue;
    const info = candidate as RivalSource;
    if (
      typeof info.horseId !== 'number' ||
      !horses.some((horse) => horse.id === info.horseId) ||
      !STYLES.includes(info.style ?? '')
    ) continue;
    rivals.push({
      horseId: info.horseId,
      league: clampNumber(info.league, 0, 2, 0),
      style: info.style!,
      polish: Math.max(0.75, Math.min(0.97, typeof info.polish === 'number' ? info.polish : 0.85)),
      statFloor: isLegacy ? { speed: 1, stamina: 1, accel: 1, heart: 1 } : clampStats(info.statFloor, 1),
    });
  }
  if (rivals.length === 0) {
    const maxSoFar = horses.reduce((best, horse) => Math.max(best, horse.id), 0);
    const roster = makeRivals(seed, maxSoFar + 1);
    horses = [...horses, ...roster.horses];
    rivals = roster.rivals;
  }
  studIds = horses
    .filter((horse) => horse.location === 'external' && horse.retired && horse.sex === 'M')
    .map((horse) => horse.id);
  // Founding studs are always retired externals; keep any rival retirees too.
  if (studIds.length === 0) {
    studIds = horses.filter((h) => h.location === 'external' && h.id < 0).map((h) => h.id);
  }
  // Exactly one featured horse, agreed on by id and location.
  let featuredId =
    typeof raw.featuredId === 'number' ? clampNumber(raw.featuredId, 1, 1_000_000, 0) : 0;
  const byLocation = horses.filter((horse) => horse.location === 'featured');
  const declared = horses.find((horse) => horse.id === featuredId && !horse.retired);
  const chosen = declared ?? byLocation.find((horse) => !horse.retired) ?? null;
  for (const horse of horses) {
    if (horse.location === 'sold' || horse.location === 'external') continue;
    horse.location = chosen && horse.id === chosen.id ? 'featured' : 'barn';
  }
  featuredId = chosen?.id ?? 0;

  const maxId = horses.reduce((best, horse) => Math.max(best, horse.id), 0);

  const yearlingsRaw = (Array.isArray(raw.yearlings) ? raw.yearlings : [])
    .map((entry) => clampYearling(entry, seed))
    .filter((lot): lot is YearlingLot => lot !== null)
    .slice(0, 4);

  const ownedIds = new Set(
    horses
      .filter((horse) => horse.location === 'featured' || horse.location === 'barn')
      .map((horse) => horse.id),
  );
  const entries: RanchState['entries'] = [];
  const seenEvents = new Set<number>();
  const seenHorseDays = new Set<string>();
  if (!isLegacy && Array.isArray(raw.entries)) {
    for (const candidate of raw.entries as unknown[]) {
      if (!candidate || typeof candidate !== 'object') continue;
      const source = candidate as { eventId?: unknown; horseId?: unknown };
      if (!Number.isInteger(source.eventId) || !Number.isInteger(source.horseId)) continue;
      const event = source.eventId as number;
      const horseId = source.horseId as number;
      const horseDay = `${horseId}:${eventDay(event)}`;
      if (
        !isValidEvent(event) ||
        eventDay(event) < day ||
        !ownedIds.has(horseId) ||
        seenEvents.has(event) ||
        seenHorseDays.has(horseDay)
      ) continue;
      entries.push({ eventId: event, horseId });
      seenEvents.add(event);
      seenHorseDays.add(horseDay);
      if (entries.length >= 32) break;
    }
  }

  const legacyEntryRefund: number = isLegacy && Array.isArray(raw.entries)
    ? (raw.entries as unknown[]).reduce<number>((sum, candidate) => {
        if (!Number.isInteger(candidate)) return sum;
        const league = eventLeague(candidate as number);
        return sum + (LEAGUE_ECONOMY[league]?.entryFeeCents ?? 0);
      }, 0)
    : 0;

  const dailyTrainingCounts: Record<string, number> = {};
  if (!isLegacy && raw.dailyTrainingCounts && typeof raw.dailyTrainingCounts === 'object') {
    for (const [key, value] of Object.entries(raw.dailyTrainingCounts).slice(0, 128)) {
      if (!/^-?\d+:(gallopTrack|enduranceTrail|startingGate|roundPen)$/.test(key)) continue;
      const count = clampNumber(value, 0, 20, 0);
      if (count > 0) dailyTrainingCounts[key] = count;
    }
  }
  if (!isLegacy && raw.pendingTraining) {
    const pending = raw.pendingTraining as Partial<PendingTraining>;
    if (typeof pending.horseId === 'number' && FACILITY_IDS.includes(pending.facility as FacilityId)) {
      const key = `${pending.horseId}:${pending.facility}`;
      const count = Math.max(0, (dailyTrainingCounts[key] ?? 1) - 1);
      if (count === 0) delete dailyTrainingCounts[key];
      else dailyTrainingCounts[key] = count;
    }
  }

  const fallbackLogHorseId = featuredId || horses.find((horse) => ownedIds.has(horse.id))?.id || 1;

  const state: RanchState = {
    meta: {
      seed,
      tick: clampNumber(raw.meta?.tick, 0, Number.MAX_SAFE_INTEGER, 0),
      day,
    },
    horses,
    featuredId: featuredId === 0 ? null : featuredId,
    nextHorseId: Math.max(clampNumber(raw.nextHorseId, 1, 2_000_000, maxId + 1), maxId + 1),
    studIds,
    rivals,
    pendingFoals: (Array.isArray(raw.pendingFoals) ? raw.pendingFoals : [])
      .filter(
        (pending): pending is { damId: number; sireId: number } =>
          typeof pending === 'object' &&
          pending !== null &&
          typeof (pending as { damId?: unknown }).damId === 'number' &&
          typeof (pending as { sireId?: unknown }).sireId === 'number',
      )
      .slice(0, 16),
    stableLevel: clampNumber(raw.stableLevel, 1, 4, 1),
    yearlings:
      yearlingsRaw.length > 0 || Array.isArray(raw.yearlings)
        ? yearlingsRaw
        : rollYearlings(seed, seasonOf(day), 0),
    energy: {
      max: maxEnergy,
      current: currentEnergy,
    },
    economy: {
      moneyCents: Math.min(
        1_000_000_000,
        clampNumber(raw.economy?.moneyCents, 0, 1_000_000_000, 0) + legacyEntryRefund,
      ),
      feedPlan,
    },
    facilities,
    entries,
    record: {
      starts: clampNumber(raw.record?.starts, 0, 1_000_000, 0),
      winsByLeague: clampWins(raw.record?.winsByLeague),
      earningsCents: clampNumber(raw.record?.earningsCents, 0, 1_000_000_000, 0),
      log: (Array.isArray(raw.record?.log) ? raw.record.log : []).slice(0, 50).map((entry) => ({
        day: (() => {
          const loggedDay = clampNumber(entry?.day, 1, 1_000_000, 1);
          if (!isLegacy) return loggedDay;
          const loggedSeason = Math.floor((loggedDay - 1) / 24) + 1;
          const loggedSeasonDay = ((loggedDay - 1) % 24) + 1;
          return (loggedSeason - 1) * 12 + Math.ceil(loggedSeasonDay / 2);
        })(),
        horseId:
          !isLegacy && typeof (entry as Partial<RaceLogEntry>)?.horseId === 'number'
            ? clampNumber((entry as Partial<RaceLogEntry>).horseId, -8, 1_000_000, fallbackLogHorseId)
            : fallbackLogHorseId,
        league: clampNumber(entry?.league, 0, 3, 0),
        place: clampNumber(entry?.place, 1, 8, 5),
        timeS:
          typeof entry?.timeS === 'number' && Number.isFinite(entry.timeS)
            ? Math.max(0, Math.min(600, entry.timeS))
            : null,
        purseCents: clampNumber(entry?.purseCents, 0, 100_000_000, 0),
      })),
    },
    racedHorseIdsToday:
      !isLegacy && Array.isArray(raw.racedHorseIdsToday)
        ? (raw.racedHorseIdsToday as unknown[])
            .filter((id): id is number => typeof id === 'number' && ownedIds.has(id))
            .slice(0, 16)
        : [],
    raceDoneToday:
      !isLegacy && Array.isArray(raw.racedHorseIdsToday)
        ? raw.racedHorseIdsToday.length > 0
        : raw.raceDoneToday === true,
    drillsToday: Math.max(0, clampNumber(raw.drillsToday, 0, 100, 0) - (raw.pendingTraining ? 1 : 0)),
    dailyTrainingCounts,
    vetLevel: clampNumber(raw.vetLevel, 0, 3, 0),
    patchUpSeason: clampNumber(raw.patchUpSeason, 0, 1_000_000, 0),
    pendingTraining: null,
  };
  return RanchSim.fromState(state);
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function saveGame(sim: RanchSim): void {
  if (!persistenceEnabled) return;
  const store = storage();
  if (!store) return;
  try {
    const current = store.getItem(SAVE_KEY);
    if (current !== null) store.setItem(BACKUP_KEY, current);
    store.setItem(SAVE_KEY, JSON.stringify({ ...serializeRanch(sim), savedAt: Date.now() }));
  } catch {
    // Storage full or blocked — the game keeps running, just unsaved.
  }
}

export function loadGame(): RanchSim | null {
  if (!persistenceEnabled) return null;
  const store = storage();
  if (!store) return null;
  for (const key of [SAVE_KEY, BACKUP_KEY, LEGACY_SAVE_KEY, LEGACY_BACKUP_KEY]) {
    try {
      const raw = store.getItem(key);
      if (raw === null) continue;
      return deserializeRanch(JSON.parse(raw));
    } catch {
      // Fall through to the backup, then to a fresh game.
    }
  }
  return null;
}

export function hasSave(): boolean {
  const store = storage();
  if (!store) return false;
  try {
    return store.getItem(SAVE_KEY) !== null || store.getItem(LEGACY_SAVE_KEY) !== null;
  } catch {
    return false;
  }
}

export function clearSave(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(SAVE_KEY);
    store.removeItem(BACKUP_KEY);
    store.removeItem(LEGACY_SAVE_KEY);
    store.removeItem(LEGACY_BACKUP_KEY);
  } catch {
    // Nothing to do.
  }
}
